"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Battery, ToggleRight, Cog, RotateCw, Timer, GitBranch, Repeat, Binary, Calendar, Users, Box, GraduationCap, Sparkles, ShieldAlert, ChevronRight, Globe, Compass, Rocket, Wrench, BrainCircuit, Presentation, Award, Layers, CircleCheck as CheckCircle2, Cpu, MonitorOff, Tablet, Receipt, Info, Check, Zap, CirclePlay as PlayCircle, CircleArrowRight as ArrowRightCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface SkillItem {
  icon: React.ComponentType<{
    className?: string;
  }>;
  labelVi: string;
  labelEn: string;
  descVi: string;
  descEn: string;
}
const keySkills: SkillItem[] = [{
  icon: Battery,
  labelVi: "Pin",
  labelEn: "Battery & Power",
  descVi: "Tìm hiểu nguồn năng lượng cung cấp cho hệ thống máy vận hành.",
  descEn: "Understand energy sources and how power drives mechanical systems."
}, {
  icon: ToggleRight,
  labelVi: "Công tắc",
  labelEn: "Switches & Controls",
  descVi: "Nguyên lý đóng/ngắt mạch và điều khiển dòng điện.",
  descEn: "Circuit open/close principles and electrical circuit control."
}, {
  icon: Cog,
  labelVi: "Động cơ",
  labelEn: "Electric Motors",
  descVi: "Chuyển đổi năng lượng điện thành chuyển động cơ học hữu ích.",
  descEn: "Convert electrical energy into precise mechanical motion."
}, {
  icon: RotateCw,
  labelVi: "Quay thuận / ngược",
  labelEn: "Forward / Reverse Rotation",
  descVi: "Đảo chiều dòng điện để điều khiển hướng quay bánh răng và động cơ.",
  descEn: "Reverse polarity to change gear drive and motor rotational directions."
}, {
  icon: Timer,
  labelVi: "Timed command cards",
  labelEn: "Timed Command Cards",
  descVi: "Thẻ lệnh định thời gian giúp phân đoạn hành vi của robot theo chu kỳ.",
  descEn: "Timed physical sequence cards that trigger periodic robot actions."
}, {
  icon: Binary,
  labelVi: "Input → Process → Output",
  labelEn: "Input → Process → Output",
  descVi: "Mô hình tư duy nền tảng: Nhận tín hiệu → Xử lý logic → Thực thi kết quả.",
  descEn: "Core foundational architecture: Input signal → Process → Executed output."
}, {
  icon: Repeat,
  labelVi: "Lặp (Loop)",
  labelEn: "Loops",
  descVi: "Tự động hóa hành vi lặp đi lặp lại một cách thông minh và tối ưu.",
  descEn: "Automate repetitive robotic sequences efficiently and reliably."
}, {
  icon: GitBranch,
  labelVi: "Nếu… thì… (If / Else)",
  labelEn: "If… Then… Logic",
  descVi: "Ra quyết định có điều kiện khi robot nhận diện trạng thái cảm biến.",
  descEn: "Conditional branching logic responding to real-time robot sensors."
}];
const methodologySteps = [{
  icon: Rocket,
  step: "01",
  titleVi: "Khởi động",
  titleEn: "Warm-up & Discovery",
  descVi: "Tìm hiểu bài toán thực tế, quan sát chuyển động tự nhiên và kích hoạt tư duy khám phá mạch điện.",
  descEn: "Explore real-world challenges, observe natural movements, and activate curiosity about electrical circuits."
}, {
  icon: Wrench,
  step: "02",
  titleVi: "Lắp ráp",
  titleEn: "Hands-on Build",
  descVi: "Lắp ráp mô hình cơ khí chính xác với khối Duplo, đấu nối pin, công tắc và trục động cơ truyền động.",
  descEn: "Assemble precise Duplo mechanical models, integrate battery packs, switches, and motor transmission shafts."
}, {
  icon: BrainCircuit,
  step: "03",
  titleVi: "Lập trình & Tư duy",
  titleEn: "Algorithm & Logic",
  descVi: "Ứng dụng chuỗi thẻ lệnh vật lý theo kiến trúc Input → Process → Output, thiết lập vòng lặp và điều kiện rẽ nhánh.",
  descEn: "Apply physical command sequences via Input → Process → Output architecture, setting loops and branching logic."
}, {
  icon: Presentation,
  step: "04",
  titleVi: "Trình bày",
  titleEn: "Presentation & Debrief",
  descVi: "Thuyết trình cơ chế hoạt động, kiểm thử phản xạ cơ học và đúc kết bài học kỹ thuật sau mỗi buổi.",
  descEn: "Explain mechanical principles, test robotic behaviors, and consolidate key engineering takeaways."
}];
const sessionPhases = [{
  phaseVi: "Giai đoạn 1: Khởi động & Nhận diện mạch",
  phaseEn: "Phase 1: Warm-up & Electrical Awareness",
  badgeVi: "Buổi 1 – 2",
  badgeEn: "Sessions 1 – 2",
  focusVi: "Khám phá nguyên lý năng lượng, an toàn điện cho trẻ mầm non, kết nối dây dẫn và tín hiệu nguồn.",
  focusEn: "Explore energy principles, child-safe electrical concepts, wire terminals, and power flow."
}, {
  phaseVi: "Giai đoạn 2: Xây dựng (Pin, Công tắc, Động cơ điện)",
  phaseEn: "Phase 2: Construction (Battery, Switch, Electric Motor)",
  badgeVi: "Buổi 3 – 5",
  badgeEn: "Sessions 3 – 5",
  focusVi: "Tích hợp hộp pin, chuyển mạch đóng/ngắt, động cơ xoay thuận nghịch và cơ cấu bánh răng chuyển động.",
  focusEn: "Integrate battery boxes, on/off switches, bidirectional DC motors, and mechanical gear trains."
}, {
  phaseVi: "Giai đoạn 3: Tư duy thuật toán",
  phaseEn: "Phase 3: Algorithmic Thinking",
  badgeVi: "Buổi 6 – 9",
  badgeEn: "Sessions 6 – 9",
  focusVi: "Thực hành thẻ lệnh định thời, cấu trúc Input → Process → Output, vòng lặp cơ bản và cấu trúc Nếu... thì...",
  focusEn: "Apply timed command cards, Input → Process → Output schema, repeating loops, and conditional If... then... logic."
}, {
  phaseVi: "Giai đoạn 4: Thử nghiệm & Tối ưu",
  phaseEn: "Phase 4: Testing & Optimization",
  badgeVi: "Buổi 10 – 11",
  badgeEn: "Sessions 10 – 11",
  focusVi: "Kiểm tra tải cơ học, gỡ lỗi logic chuyển động, điều chỉnh độ trễ thời gian và cân bằng robot khi di chuyển.",
  focusEn: "Test load tolerance, debug algorithmic sequences, calibrate timing intervals, and optimize chassis stability."
}, {
  phaseVi: "Giai đoạn 5: Dự án cuối khóa / Tốt nghiệp Duplo",
  phaseEn: "Phase 5: Capstone Project & Duplo Graduation",
  badgeVi: "Buổi 12",
  badgeEn: "Session 12",
  focusVi: "Chế tạo robot tổng hợp hoàn chỉnh, tham gia 'Ngày hội robot – tốt nghiệp Duplo' và nhận chứng chỉ chuyển tiếp HP1.",
  focusEn: "Build comprehensive robotic models, showcase in 'Robot Festival – Bridge Capstone', and receive the HP1 promotion certificate."
}];
const pathwayNodes = [{
  code: "D1",
  nameVi: "Khám phá thế giới",
  nameEn: "World Discovery",
  href: "/RoboticsCourseD1Page",
  stage: "Duplo",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-sizes"
}, {
  code: "D2",
  nameVi: "Cơ cấu đơn giản",
  nameEn: "Simple Mechanisms",
  href: "/RoboticsCourseD2Page",
  stage: "Duplo",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-sizes"
}, {
  code: "D3",
  nameVi: "Cỗ máy kỳ diệu",
  nameEn: "Amazing Machines",
  href: "/RoboticsCourseD3Page",
  stage: "Duplo",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-sizes"
}, {
  code: "D4",
  nameVi: "Động cơ nhí",
  nameEn: "Junior Motors",
  href: "#",
  stage: "Duplo",
  current: true,
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-sizes"
}, {
  code: "HP1",
  nameVi: "Kỹ sư cơ khí nhí",
  nameEn: "Junior Mechanical",
  href: "/RoboticsCourseHP1Page",
  stage: "WhalesBot",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-sizes"
}, {
  code: "HP2",
  nameVi: "Cảm biến & Tự động",
  nameEn: "Sensors & Auto",
  href: "/RoboticsCourseHP2Page",
  stage: "WhalesBot",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-sizes"
}, {
  code: "HP3",
  nameVi: "Lập trình thẻ bài",
  nameEn: "Coding Cards",
  href: "/RoboticsCourseHP3Page",
  stage: "WhalesBot",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-sizes"
}, {
  code: "HP4",
  nameVi: "Robot thông minh",
  nameEn: "Smart Robotics",
  href: "/RoboticsCourseHP4Page",
  stage: "WhalesBot",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-sizes"
}, {
  code: "HP5",
  nameVi: "Sáng tạo mở rộng",
  nameEn: "Advanced Builds",
  href: "/RoboticsCourseHp5Page",
  stage: "WhalesBot",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-sizes"
}, {
  code: "HP6",
  nameVi: "Chinh phục đấu trường",
  nameEn: "Competition Arena",
  href: "/RoboticsCourseHP6Page",
  stage: "WhalesBot",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-sizes"
}];
function CourseConsumer() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  // Hook consumption from PostsProvider
  const {
    posts
  } = usePosts();
  const isVi = lang === "vi";
  return <div className="space-y-12">
      {/* (1) Four-Level Breadcrumb & Language Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/" className="font-medium text-muted-foreground hover:text-foreground transition-colors">
                  {isVi ? "Trang chủ" : "Home"}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/RoboticsLearningPathwayPage" className="font-medium text-muted-foreground hover:text-foreground transition-colors">
                  {isVi ? "Lộ trình Robotics" : "Robotics Pathway"}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="#" className="font-medium text-muted-foreground hover:text-foreground transition-colors">
                  {isVi ? "Robotics 4–6 tuổi" : "Robotics 4–6 Years"}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-semibold text-foreground">
                {isVi ? "D4 – Động cơ nhí" : "D4 – Junior Motors"}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* VI / EN Toggle */}
        <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
          <Globe className="size-4 text-muted-foreground" />
          <div className="inline-flex rounded-lg border border-border bg-card p-0.5 shadow-2xs">
            <Button variant={isVi ? "default" : "ghost"} size="sm" className="h-7 px-2.5 text-xs font-semibold rounded-md" onClick={() => setLang("vi")}>
              Tiếng Việt
            </Button>
            <Button variant={!isVi ? "default" : "ghost"} size="sm" className="h-7 px-2.5 text-xs font-semibold rounded-md" onClick={() => setLang("en")}>
              English
            </Button>
          </div>
        </div>
      </div>

      {/* Main Hero Card for D4 with Top Technical Visual */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 md:p-10 shadow-md">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Hero Column */}
          <div className="lg:col-span-7 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="default" className="text-sm px-3 py-1 font-bold tracking-wider">
                D4
              </Badge>
              <Badge variant="secondary" className="text-xs sm:text-sm px-3.5 py-1 font-bold uppercase tracking-wide bg-secondary text-secondary-foreground">
                <Sparkles className="size-3.5 mr-1.5" />
                {isVi ? "CẦU NỐI SANG ROBOTICS LẬP TRÌNH" : "BRIDGE TO PROGRAMMABLE ROBOTICS"}
              </Badge>
              <Badge variant="outline" className="text-xs px-3 py-0.75 font-semibold text-muted-foreground">
                {isVi ? "KHÓA DUPLO CUỐI CÙNG" : "FINAL DUPLO COURSE"}
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
              {isVi ? "Động cơ nhí" : "Junior Motors"}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              {isVi ? "Cột mốc chuyển giao then chốt giúp trẻ từ lắp ráp cơ khí tĩnh bước sang điều khiển động cơ điện và làm quen với tư duy lập trình sơ khởi thông qua kiến trúc Input → Process → Output." : "The critical milestone transitioning young students from static mechanics to motorized systems and foundational algorithmic logic via the Input → Process → Output paradigm."}
            </p>

            {/* Hero CTAs */}
            <div className="pt-2 flex flex-wrap items-center gap-3.5">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold px-6">
                <Link to="/FreeTrialContactPage">
                  {isVi ? "ĐĂNG KÝ TƯ VẤN" : "BOOK CONSULTATION"}
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold px-6">
                <Link to="/RoboticsLearningPathwayPage">
                  <Compass className="size-4 mr-1.5" />
                  {isVi ? "XEM TOÀN BỘ LỘ TRÌNH" : "VIEW FULL PATHWAY"}
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Hero Visual Panel (Motor & Battery Concepts in Deep Tech Branding) */}
          <div className="lg:col-span-5">
            <div className="rounded-xl border border-slate-800 bg-slate-950 p-4 text-slate-100 shadow-lg relative overflow-hidden group">
              {/* Header Spec Tag */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-3">
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs font-mono font-bold tracking-wider uppercase text-slate-300">
                    {isVi ? "CƠ CẤU ĐỘNG CƠ DUPLO" : "MOTORIZED DUPLO MECHANISM"}
                  </span>
                </div>
                <Badge variant="outline" className="border-slate-700 text-slate-300 text-[10px] font-mono">
                  9V DC / BATTERY PACK
                </Badge>
              </div>

              {/* Mechanism Image Visual */}
              <div className="relative rounded-lg overflow-hidden border border-slate-800 bg-slate-900 aspect-4/3 mb-3">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_motor_battery_mechanism.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_motor_battery_mechanism.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_motor_battery_mechanism.png-wvc-sizes" alt="Motorized Duplo mechanism with battery pack and motor concept callouts" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent pointer-events-none" />

                {/* Overlaid Tech Callout Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                  <div className="bg-slate-900/90 backdrop-blur-md border border-slate-700 px-2.5 py-1 rounded-md text-[11px] font-semibold text-emerald-400 flex items-center gap-1.5 shadow-sm">
                    <Battery className="size-3.5 text-emerald-400" />
                    <span>{isVi ? "Hộp Pin An Toàn 9V" : "Safety 9V Battery Pack"}</span>
                  </div>
                  <div className="bg-slate-900/90 backdrop-blur-md border border-slate-700 px-2.5 py-1 rounded-md text-[11px] font-semibold text-amber-400 flex items-center gap-1.5 shadow-sm">
                    <Cog className="size-3.5 text-amber-400 animate-spin" style={{
                    animationDuration: '8s'
                  }} />
                    <span>{isVi ? "Động cơ điện DC" : "9V DC Electric Motor"}</span>
                  </div>
                </div>

                <div className="absolute bottom-3 right-3 bg-slate-900/90 backdrop-blur-md border border-slate-700 px-2.5 py-1 rounded-md text-[11px] font-semibold text-sky-400 flex items-center gap-1.5 shadow-sm">
                  <ToggleRight className="size-3.5 text-sky-400" />
                  <span>{isVi ? "Công tắc Đảo Chiều" : "Reversible Polarity Switch"}</span>
                </div>
              </div>

              {/* Bottom Visual Description */}
              <div className="grid grid-cols-3 gap-2 pt-1 text-center font-mono text-[11px]">
                <div className="bg-slate-900/80 rounded p-1.5 border border-slate-800">
                  <span className="text-slate-400 block text-[9px]">{isVi ? "Năng Lượng" : "Power"}</span>
                  <span className="text-emerald-400 font-bold">{isVi ? "Pin 9V" : "9V DC"}</span>
                </div>
                <div className="bg-slate-900/80 rounded p-1.5 border border-slate-800">
                  <span className="text-slate-400 block text-[9px]">{isVi ? "Điều Khiển" : "Control"}</span>
                  <span className="text-sky-400 font-bold">{isVi ? "Công Tắc" : "Switch"}</span>
                </div>
                <div className="bg-slate-900/80 rounded p-1.5 border border-slate-800">
                  <span className="text-slate-400 block text-[9px]">{isVi ? "Tác Động" : "Action"}</span>
                  <span className="text-amber-400 font-bold">{isVi ? "Quay Bánh Răng" : "Gear Drive"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Specs Grid */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-xl border border-border bg-background/60 p-4 shadow-2xs">
            <div className="flex items-center gap-2 text-primary mb-1">
              <Users className="size-4.5" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {isVi ? "Độ tuổi" : "Age Range"}
              </span>
            </div>
            <div className="text-base sm:text-lg font-bold text-foreground">
              {isVi ? "5–6 tuổi" : "5–6 years old"}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background/60 p-4 shadow-2xs">
            <div className="flex items-center gap-2 text-primary mb-1">
              <Box className="size-4.5" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {isVi ? "Nền tảng" : "Platform"}
              </span>
            </div>
            <div className="text-base sm:text-lg font-bold text-foreground">
              Duplo
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background/60 p-4 shadow-2xs">
            <div className="flex items-center gap-2 text-primary mb-1">
              <SafeHydrate><Calendar className="size-4.5" /></SafeHydrate>
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {isVi ? "Thời lượng" : "Duration"}
              </span>
            </div>
            <div className="text-base sm:text-lg font-bold text-foreground">
              {isVi ? "12 buổi × 90 phút" : "12 sessions × 90 mins"}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background/60 p-4 shadow-2xs">
            <div className="flex items-center gap-2 text-primary mb-1">
              <GraduationCap className="size-4.5" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {isVi ? "Quy mô nhóm" : "Group Size"}
              </span>
            </div>
            <div className="text-base sm:text-lg font-bold text-foreground">
              {isVi ? "2–3 học sinh/bộ kit" : "2–3 students/kit"}
            </div>
          </div>
        </div>

        {/* Position Note highlight */}
        <div className="mt-6 rounded-xl border border-accent/40 bg-accent/10 p-4 sm:p-5 flex items-start gap-3.5">
          <Sparkles className="size-5 text-accent shrink-0 mt-0.5" />
          <div className="text-sm sm:text-base text-foreground leading-relaxed">
            <span className="font-semibold text-accent-foreground">
              {isVi ? "Ghi chú định vị: " : "Pathway Position Note: "}
            </span>
            {isVi ? "Cầu nối sang WhalesBot — trang bị vững chắc tư duy mạch điện, động cơ và chuỗi lệnh trước khi chuyển tiếp vào các học phần công nghệ chuyên sâu." : "Bridge to WhalesBot — equips students with essential circuits, motor dynamics, and procedural command skills before entering advanced tech pathways."}
          </div>
        </div>
      </div>

      {/* (2) Who is this course for? Section */}
      <div className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
            {isVi ? "Khóa học này dành cho ai?" : "Who is this course for?"}
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base">
            {isVi ? "Chương trình chuyên biệt cho trẻ 5–6 tuổi chuẩn bị bước vào kỷ nguyên Robotics lập trình." : "Specially tailored for 5–6 year olds preparing for programmable robotic platforms."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Card className="border-border bg-card p-6 shadow-2xs">
            <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
              <Users className="size-6" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">
              {isVi ? "Độ tuổi 5–6 tuổi" : "Ages 5–6"}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {isVi ? "Dành cho học sinh mẫu giáo lớn đã làm quen với các cấu trúc gạch xây dựng cơ bản và sẵn sàng tiếp cận thiết bị cơ điện an toàn." : "Designed for kindergarten students ready to transition from manual building blocks into safe, motorized robotics systems."}
            </p>
          </Card>

          <Card className="border-border bg-card p-6 shadow-2xs">
            <div className="size-11 rounded-lg bg-secondary/20 text-secondary-foreground flex items-center justify-center mb-4">
              <Rocket className="size-6" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">
              {isVi ? "Cầu nối sang Robotics HP1" : "Bridge to Programmable HP1"}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {isVi ? "Khóa học bản lề giúp trẻ chuẩn bị tâm thế vững vàng, chuyển đổi liền mạch từ thao tác cơ học tĩnh sang lập trình robot thông minh với WhalesBot." : "The pivotal bridge course preparing learners for algorithmic thinking and modular robotics in the WhalesBot AI Module 1S series."}
            </p>
          </Card>

          <Card className="border-border bg-card p-6 shadow-2xs">
            <div className="size-11 rounded-lg bg-accent/20 text-accent-foreground flex items-center justify-center mb-4">
              <Binary className="size-6" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">
              {isVi ? "Mạch điện & Input → Process → Output" : "Power & Input → Process → Output"}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {isVi ? "Nắm vững bản chất dòng điện, công tắc, động cơ điện và làm quen với mô hình tư duy tính toán cốt lõi: Nhận tín hiệu → Xử lý → Thực thi." : "Master battery circuits, motor direction, and computational fundamentals through the Input → Process → Output architecture."}
            </p>
          </Card>
        </div>
      </div>

      {/* Mid-Page Technical Diagram Visual: Input → Process → Output Core Paradigm */}
      <Card className="border border-slate-800 bg-slate-950 text-slate-100 p-6 sm:p-8 rounded-2xl shadow-xl overflow-hidden relative">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-800">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 bg-emerald-500/10 font-mono text-xs uppercase px-2.5 py-0.5">
                <BrainCircuit className="size-3.5 mr-1 text-emerald-400" />
                {isVi ? "MÔ HÌNH TƯ DUY CỐT LÕI" : "CORE CONCEPT ARCHITECTURE"}
              </Badge>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-white">
              INPUT → PROCESS → OUTPUT
            </h3>
            <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-2xl">
              {isVi ? "Nguyên lý vận hành cơ bản của mọi cỗ máy tự động: Tín hiệu công tắc / cảm biến (Input) → Thẻ lệnh / Mạch xử lý (Process) → Động cơ quay bánh xe (Output)." : "The fundamental execution paradigm of automated machines: Switch / Sensor Signal (Input) → Command Card / Logic Circuit (Process) → Motor Wheel Drive (Output)."}
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2 bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-lg text-xs font-mono text-slate-300">
            <Zap className="size-4 text-amber-400" />
            <span>ROBOTO ARCHITECTURE SPEC</span>
          </div>
        </div>

        {/* Input Process Output Flow Diagram Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center pt-6">
          {/* Visual Diagram Image */}
          <div className="lg:col-span-5 rounded-xl border border-slate-800 bg-slate-900 p-3 overflow-hidden">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/input_process_output_diagram.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/input_process_output_diagram.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/input_process_output_diagram.png-wvc-sizes" alt="Input Process Output flowchart: Button press to timed logic to motor wheel rotation" loading="lazy" className="w-full h-auto object-cover rounded-lg" />
          </div>

          {/* Interactive Flow Step Cards */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Step 1: Input */}
            <div className="rounded-xl border border-sky-500/30 bg-sky-950/20 p-4 relative flex flex-col justify-between">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-sky-500/20 text-sky-300 font-bold">
                    STEP 01
                  </span>
                  <ToggleRight className="size-5 text-sky-400" />
                </div>
                <h4 className="text-base font-bold text-sky-200">
                  INPUT
                </h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  {isVi ? "Nhấn nút / Gạt công tắc / Tín hiệu nguồn" : "Button press / Switch toggle / Power signal"}
                </p>
              </div>
              <div className="mt-3 pt-2 border-t border-sky-900/50 text-[11px] font-mono text-sky-400 flex items-center gap-1">
                <PlayCircle className="size-3" />
                <span>{isVi ? "Kích hoạt dòng điện" : "Power Trigger"}</span>
              </div>
            </div>

            {/* Step 2: Process */}
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4 relative flex flex-col justify-between">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">
                    STEP 02
                  </span>
                  <Timer className="size-5 text-emerald-400" />
                </div>
                <h4 className="text-base font-bold text-emerald-200">
                  PROCESS
                </h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  {isVi ? "Thẻ lệnh định thời / Vòng lặp / Logic rẽ nhánh" : "Timed cards / Loop sequence / Conditional branch"}
                </p>
              </div>
              <div className="mt-3 pt-2 border-t border-emerald-900/50 text-[11px] font-mono text-emerald-400 flex items-center gap-1">
                <BrainCircuit className="size-3" />
                <span>{isVi ? "Xử lý quy trình" : "Logic Flow"}</span>
              </div>
            </div>

            {/* Step 3: Output */}
            <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 p-4 relative flex flex-col justify-between">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold">
                    STEP 03
                  </span>
                  <Cog className="size-5 text-amber-400" />
                </div>
                <h4 className="text-base font-bold text-amber-200">
                  OUTPUT
                </h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  {isVi ? "Động cơ quay / Bánh xe chuyển động / Robot di chuyển" : "Motor rotation / Wheels turn / Robot drives"}
                </p>
              </div>
              <div className="mt-3 pt-2 border-t border-amber-900/50 text-[11px] font-mono text-amber-400 flex items-center gap-1">
                <ArrowRightCircle className="size-3" />
                <span>{isVi ? "Thực thi cơ học" : "Mechanical Motion"}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* NEW BLOCK (1): Equipment & Hardware Details Card */}
      <Card className="border-border bg-card p-6 md:p-8 shadow-2xs overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center pb-6 border-b border-border">
          <div className="lg:col-span-6 space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-primary/30 text-primary font-bold text-xs uppercase px-2.5 py-0.5">
                <Box className="size-3 mr-1" />
                {isVi ? "TRANG THIẾT BỊ HỌC TẬP" : "LEARNING HARDWARE & SPECS"}
              </Badge>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
              {isVi ? "Nền tảng Duplo (LEGO® Education)" : "Duplo Platform (LEGO® Education)"}
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {isVi ? "Hệ thống giáo cụ tiêu chuẩn quốc tế giúp học sinh mầm non tiếp cận trực quan với cơ điện tử thông qua các linh kiện an toàn, chống va đập và tối ưu cho bàn tay nhỏ." : "International standard educational kits enabling preschool children to intuitively discover electromechanics through child-safe, durable modular hardware."}
            </p>
          </div>

          {/* Equipment Kit Image */}
          <div className="lg:col-span-3 flex items-center justify-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-sizes" alt="LEGO Duplo STEAM robotics kit used in ROBOTO's D1–D4 foundation track" loading="lazy" className="w-full max-w-[220px] lg:max-w-none h-auto object-contain rounded-lg" />
          </div>

          <div className="lg:col-span-3 shrink-0 rounded-xl border border-primary/20 bg-primary/5 p-4 sm:p-5 flex flex-col justify-center">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
              <Receipt className="size-3.5 text-primary" />
              <span>{isVi ? "Chi phí thiết bị tham khảo" : "Reference Equipment Cost"}</span>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-primary font-serif">
              ~1.000.000 VNĐ
              <span className="text-xs font-medium text-muted-foreground ml-1">/kit</span>
            </div>
            <div className="mt-2 text-[11px] leading-tight text-muted-foreground/90 flex items-center gap-1">
              <Info className="size-3 text-primary shrink-0" />
              <span>{isVi ? "* Đây là chi phí thiết bị tham khảo, không phải học phí" : "* Reference equipment cost, not tuition fee"}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6">
          <div className="rounded-xl border border-border bg-background/50 p-4 sm:p-5 flex items-start gap-3.5">
            <div className="size-10 rounded-lg bg-secondary/15 text-secondary flex items-center justify-center shrink-0">
              <MonitorOff className="size-5" />
            </div>
            <div className="space-y-1">
              <div className="text-sm sm:text-base font-bold text-foreground">
                {isVi ? "Không yêu cầu máy tính" : "Computer NOT required"}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {isVi ? "Học sinh thao tác trực tiếp 100% trên thẻ lệnh vật lý, công tắc và cơ cấu động cơ thực tế, không đòi hỏi trang bị máy tính để bàn hay laptop." : "Students engage 100% hands-on with physical logic cards, mechanical switches, and motor components without needing a laptop or PC."}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background/50 p-4 sm:p-5 flex items-start gap-3.5">
            <div className="size-10 rounded-lg bg-accent/15 text-accent-foreground flex items-center justify-center shrink-0">
              <Tablet className="size-5" />
            </div>
            <div className="space-y-1">
              <div className="text-sm sm:text-base font-bold text-foreground">
                {isVi ? "Máy tính bảng: Thiết bị bổ trợ tùy chọn" : "Tablet: Optional supplementary device"}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {isVi ? "Chỉ đóng vai trò công cụ hỗ trợ hình ảnh trực quan hoặc tra cứu chuyển động khi cần; hoàn toàn không bắt buộc trong quá trình học và thực hành." : "Acts solely as an optional visual aid or motion reference when helpful; never mandatory for core classroom builds or learning milestones."}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* NEW BLOCK (2): Small-group Learning Model Section */}
      <div className="space-y-6">
        <div className="space-y-1">
          <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-bold text-xs uppercase px-2.5 py-0.5">
            {isVi ? "MÔ HÌNH LỚP HỌC KỸ THUẬT" : "ENGINEERING CLASSROOM STRUCTURE"}
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
            {isVi ? "HỌC ROBOTICS THEO NHÓM NHỎ" : "SMALL-GROUP ROBOTICS LEARNING"}
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-3xl">
            {isVi ? "Hai mô hình tổ chức nhóm học được thiết kế linh hoạt nhằm đáp ứng nhu cầu cụ thể của từng trung tâm và gia đình, cân bằng giữa trải nghiệm phối hợp đồng đội và hiệu quả đầu tư giáo cụ." : "Two flexible cohort models tailored to center capacities and family needs, balancing collaborative interaction with optimal hardware allocation."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Model A: 2 students/kit */}
          <Card className="border-border bg-card p-6 sm:p-7 shadow-2xs hover:border-secondary/40 transition-colors flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="flex size-9 items-center justify-center rounded-lg bg-secondary/15 text-secondary font-bold text-base">
                    2
                  </span>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-foreground font-serif">
                      {isVi ? "2 học sinh / bộ kit" : "2 students / kit"}
                    </h3>
                    <span className="text-xs font-semibold text-secondary uppercase tracking-wider">
                      {isVi ? "Mô hình lý tưởng" : "Ideal model"}
                    </span>
                  </div>
                </div>
                <Badge variant="outline" className="border-border text-muted-foreground text-xs font-medium">
                  {isVi ? "Tỷ lệ 2:1" : "2:1 Ratio"}
                </Badge>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                {isVi ? "Mô hình tạo điều kiện cho sự phân vai nhịp nhàng giữa kỹ sư cơ khí (lắp ráp kết cấu) và kỹ sư điều khiển (đấu nối mạch điện & thẻ lệnh), đảm bảo mỗi trẻ đều có thời lượng tương tác trực tiếp tối đa với thiết bị." : "Provides balanced pair collaboration where learners alternate roles between mechanical assembly and electrical circuit control, ensuring extensive hands-on device interaction for each child."}
              </p>

              <div className="space-y-2.5 pt-2 border-t border-border/80">
                <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                  <Check className="size-4 text-secondary shrink-0 mt-0.5" />
                  <span>{isVi ? "Tập trung cao độ và luân phiên vai trò lắp ráp mạch điện" : "High personal engagement with clear alternating engineering roles"}</span>
                </div>
                <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                  <Check className="size-4 text-secondary shrink-0 mt-0.5" />
                  <span>{isVi ? "Thao tác gỡ lỗi cơ khí diễn ra nhanh chóng và liền mạch" : "Rapid hardware troubleshooting and seamless iterative testing"}</span>
                </div>
                <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                  <Check className="size-4 text-secondary shrink-0 mt-0.5" />
                  <span>{isVi ? "Phù hợp với học sinh ưa thích làm việc cặp đôi sâu sắc" : "Ideal for students seeking deep focused paired collaboration"}</span>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground flex items-center gap-2">
              <Info className="size-3.5 text-secondary shrink-0" />
              <span>{isVi ? "Được khuyến nghị cho các buổi thực hành chuyên sâu" : "Recommended for intensive project-driven labs"}</span>
            </div>
          </Card>

          {/* Model B: 3 students/kit */}
          <Card className="border-border bg-card p-6 sm:p-7 shadow-2xs hover:border-primary/40 transition-colors flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold text-base">
                    3
                  </span>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-foreground font-serif">
                      {isVi ? "3 học sinh / bộ kit" : "3 students / kit"}
                    </h3>
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                      {isVi ? "Mô hình tối ưu chi phí" : "Cost-optimized model"}
                    </span>
                  </div>
                </div>
                <Badge variant="outline" className="border-border text-muted-foreground text-xs font-medium">
                  {isVi ? "Tỷ lệ 3:1" : "3:1 Ratio"}
                </Badge>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                {isVi ? "Mô hình mở rộng tính gắn kết đồng đội qua bộ 3 nhiệm vụ: Lắp ráp cơ khí, Quản trị linh kiện mạch điện và Kiểm thử - Thuyết trình kết quả, giúp tiết kiệm chi phí giáo cụ mà vẫn giữ trọn vẹn hào hứng học tập." : "Expands team synergy across a triad of roles: mechanical construction, electrical component management, and testing/presentation debriefs, optimizing kit allocation without compromising engagement."}
              </p>

              <div className="space-y-2.5 pt-2 border-t border-border/80">
                <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>{isVi ? "Rèn luyện kỹ năng giao tiếp, thảo luận và làm việc nhóm tam giác" : "Nurtures communicative dialogue and multi-perspective team consensus"}</span>
                </div>
                <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>{isVi ? "Tối ưu hóa chi phí đầu tư thiết bị học tập cho gia đình & lớp học" : "Optimizes hardware investment budget for families and partner centers"}</span>
                </div>
                <div className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>{isVi ? "Phát huy khả năng thuyết trình và phản biện ý tưởng lẫn nhau" : "Encourages collective project debriefing and shared peer presentation"}</span>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground flex items-center gap-2">
              <Info className="size-3.5 text-primary shrink-0" />
              <span>{isVi ? "Lựa chọn phổ biến cho các lớp học trường học & trung tâm đối tác" : "Popular option for partner campuses and larger cohorts"}</span>
            </div>
          </Card>
        </div>
      </div>

      {/* Key Skills Section (Existing Content Kept) */}
      <div className="space-y-5">
        <div className="space-y-1">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
            {isVi ? "Kỹ năng trọng tâm" : "Key Skills Acquired"}
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base">
            {isVi ? "Các khái niệm kỹ thuật và thuật toán cốt lõi được đào tạo xuyên suốt 12 buổi học của D4." : "Core technical concepts and algorithmic fundamentals built progressively throughout D4."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {keySkills.map((skill, index) => {
          const Icon = skill.icon;
          return <Card key={index} data-index={index} className="border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all">
                <CardHeader className="pb-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2">
                    <Icon className="size-5" />
                  </div>
                  <CardTitle className="text-base font-bold text-foreground">
                    {isVi ? skill.labelVi : skill.labelEn}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {isVi ? skill.descVi : skill.descEn}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>

      {/* (3) 12-Session Structure Timeline */}
      <div className="space-y-6">
        <div className="space-y-1">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
            {isVi ? "Cấu trúc 12 buổi học" : "12-Session Structure Timeline"}
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base">
            {isVi ? "Lộ trình đào tạo 5 giai đoạn được thiết kế khoa học, đảm bảo trẻ tiếp thu tự nhiên từ cơ điện đến thuật toán." : "A 5-phase structured roadmap progressing sequentially from electromechanical principles to algorithmic reasoning."}
          </p>
        </div>

        <div className="space-y-4">
          {sessionPhases.map((phase, index) => <Card key={index} data-index={index} className="border-border bg-card p-5 md:p-6 shadow-2xs hover:border-secondary/50 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-2.5">
                <div className="flex items-center gap-3">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold text-sm">
                    {index + 1}
                  </span>
                  <h3 className="text-base sm:text-lg font-bold text-foreground">
                    {isVi ? phase.phaseVi : phase.phaseEn}
                  </h3>
                </div>
                <Badge variant="secondary" className="w-fit text-xs px-2.5 py-0.5 font-bold">
                  {isVi ? phase.badgeVi : phase.badgeEn}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground pl-0 sm:pl-11 leading-relaxed">
                {isVi ? phase.focusVi : phase.focusEn}
              </p>
            </Card>)}
        </div>
      </div>

      {/* (4) Learning Methodology 4-Step Flow */}
      <div className="space-y-6">
        <div className="space-y-1">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
            {isVi ? "Phương pháp học" : "Learning Methodology"}
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base">
            {isVi ? "Quy trình sư phạm 4 bước khép kín giúp học sinh phát triển năng lực tư duy kỹ thuật độc lập." : "The 4-step pedagogical cycle nurturing independent analytical thinking and engineering confidence."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {methodologySteps.map((m, index) => {
          const Icon = m.icon;
          return <Card key={index} data-index={index} className="border-border bg-card p-6 shadow-2xs relative overflow-hidden flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Icon className="size-6" />
                    </div>
                    <span className="text-2xl font-black text-muted/80 font-serif">
                      {m.step}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-foreground mb-2">
                    {isVi ? m.titleVi : m.titleEn}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {isVi ? m.descVi : m.descEn}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-border flex items-center gap-1.5 text-xs font-semibold text-secondary">
                  <CheckCircle2 className="size-3.5" />
                  <span>{isVi ? `Bước ${index + 1}` : `Stage ${index + 1}`}</span>
                </div>
              </Card>;
        })}
        </div>
      </div>

      {/* (5) & (6) Final Project and Platform / Equipment Spec Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* (5) Final Project Card */}
        <Card className="border-border bg-card p-6 sm:p-7 shadow-2xs flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Award className="size-5 text-accent" />
              <span className="text-xs font-bold uppercase tracking-wider text-accent-foreground">
                {isVi ? "Dự án / Sản phẩm cuối khóa" : "Final Project"}
              </span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-serif mb-2">
              {isVi ? "Ngày hội robot – tốt nghiệp Duplo" : "Robot Festival – Bridge Capstone"}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {isVi ? "Dự án tốt nghiệp toàn diện đánh dấu cột mốc hoàn thành chặng Duplo mầm non. Học sinh tự tay thiết kế cỗ máy tự vận hành tích hợp pin, động cơ, công tắc và áp dụng chuỗi lệnh định thời trước khi chuyển tiếp lên hệ sinh thái lập trình HP1." : "A comprehensive graduation capstone marking the culmination of the Duplo foundation track. Students independently engineer motorized robotic systems with timed command sequences, demonstrating readiness for HP1 programmable robotics."}
            </p>
          </div>
          <div className="rounded-lg bg-muted/60 p-3.5 text-xs text-foreground flex items-center gap-2 font-medium">
            <Sparkles className="size-4 text-primary shrink-0" />
            <span>
              {isVi ? "Cột mốc chuyển tiếp chính thức: Đạt chuẩn để nhập môn WhalesBot HP1." : "Official Bridge Milestone: Certified qualification for WhalesBot HP1 entry."}
            </span>
          </div>
        </Card>

        {/* (6) Platform / Equipment Spec Card */}
        <Card className="border-border bg-card p-6 sm:p-7 shadow-2xs flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Cpu className="size-5 text-primary" />
              <span className="text-xs font-bold uppercase tracking-wider text-primary">
                {isVi ? "Nền tảng / Thiết bị" : "Platform / Equipment"}
              </span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-serif mb-2">
              Duplo Motorized STEM Kit
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {isVi ? "Sử dụng hệ thống khối gạch Duplo kích thước an toàn, bổ sung động cơ điện một chiều 9V, hộp pin bảo vệ chống đoản mạch, công tắc gạt 3 vị trí và bộ thẻ lệnh biểu tượng trực quan." : "Utilizes child-safe large Duplo building blocks equipped with 9V DC electric motors, safety-shielded battery packs, 3-position toggle switches, and tangible symbolic sequence cards."}
            </p>
          </div>
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3.5 text-xs text-foreground flex items-center gap-2 font-medium">
            <Layers className="size-4 text-primary shrink-0" />
            <span>
              {isVi ? "Lưu ý nền tảng: D4 là khóa học Duplo cuối cùng trước khi học sinh chuyển giao hoàn toàn sang WhalesBot AI Module 1S." : "Platform Notice: D4 is the final Duplo course before students transition exclusively to WhalesBot AI Module 1S."}
            </span>
          </div>
        </Card>
      </div>

      {/* (7) Position in Pathway Mini Roadmap */}
      <div className="space-y-4 rounded-2xl border border-border bg-card p-6 md:p-8 shadow-2xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border pb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-foreground font-serif">
              {isVi ? "Vị trí trong lộ trình" : "Position in Pathway"}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              {isVi ? "Tổng quan 10 học phần Robotics ROBOTO từ 4 đến 16 tuổi. D4 là điểm chuyển giao trọng yếu giữa Duplo và WhalesBot." : "Complete 10-course ROBOTO pathway roadmap (ages 4–16). D4 serves as the critical bridge node between Duplo and WhalesBot."}
            </p>
          </div>
          <Button asChild variant="outline" size="sm" className="h-8 text-xs font-semibold">
            <Link to="/RoboticsLearningPathwayPage">
              {isVi ? "Xem trang lộ trình" : "Full Pathway Page"}
              <ChevronRight className="size-3.5 ml-1" />
            </Link>
          </Button>
        </div>

        {/* 10-Node Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2.5 pt-2">
          {pathwayNodes.map((node, i) => {
          const isCurrent = node.current;
          return <div key={node.code} data-index={i} className="flex flex-col">
                {isCurrent ? <div className="flex flex-col items-center justify-center p-2.5 rounded-lg border-2 border-primary bg-primary/10 text-center shadow-xs">
                    <img src={node.imgSrc} data-wvc-srcset={node.imgSrcset} data-wvc-sizes={node.imgSizes} alt={`${node.code} robotics course thumbnail`} loading="lazy" className="size-10 sm:size-11 object-cover rounded-md mb-1.5" />
                    <span className="text-xs font-bold uppercase text-primary tracking-wide">
                      {node.code}
                    </span>
                    <span className="text-[11px] font-semibold text-foreground line-clamp-1 mt-0.5">
                      {isVi ? node.nameVi : node.nameEn}
                    </span>
                    <Badge variant="default" className="mt-1 text-[9px] px-1 py-0 h-4 bg-primary text-primary-foreground font-bold">
                      {isVi ? "HIỆN TẠI" : "CURRENT"}
                    </Badge>
                  </div> : <Link to={node.href} className="flex flex-col items-center justify-center p-2.5 rounded-lg border border-border bg-background hover:border-secondary hover:bg-muted/50 text-center transition-all group">
                    <img src={node.imgSrc} data-wvc-srcset={node.imgSrcset} data-wvc-sizes={node.imgSizes} alt={`${node.code} robotics course thumbnail`} loading="lazy" className="size-10 sm:size-11 object-cover rounded-md mb-1.5 group-hover:scale-105 transition-transform" />
                    <span className="text-xs font-bold uppercase text-muted-foreground group-hover:text-foreground">
                      {node.code}
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground group-hover:text-foreground line-clamp-1 mt-0.5">
                      {isVi ? node.nameVi : node.nameEn}
                    </span>
                    <span className="text-[9px] text-muted-foreground/70 mt-1 uppercase font-mono">
                      {node.stage}
                    </span>
                  </Link>}
              </div>;
        })}
        </div>
      </div>

      {/* (9) What's next for your child? Card (Placed before next-course navigation) */}
      <Card className="border-secondary/40 bg-secondary/5 p-6 md:p-8 rounded-2xl shadow-2xs">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-bold text-xs uppercase px-2.5 py-0.5">
                {isVi ? "BƯỚC TIẾP THEO CỦA CON LÀ GÌ?" : "WHAT'S NEXT FOR YOUR CHILD?"}
              </Badge>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
              {isVi ? "HP1 – Kỹ sư cơ khí nhí" : "HP1 – Junior Mechanical Engineer"}
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {isVi ? "Sau khi tốt nghiệp D4, học sinh chính thức chuyển giao sang hệ sinh thái WhalesBot AI Module 1S. Tại HP1, trẻ sẽ làm việc với các khối ghép kỹ thuật chuyên nghiệp, hệ thống bánh răng phức hợp và động cơ điều khiển thông minh." : "Upon graduating from D4, learners transition into the WhalesBot AI Module 1S ecosystem. In HP1, students explore advanced structural engineering, complex gear ratios, and intelligent robotic drive units."}
            </p>
          </div>

          <div className="shrink-0 flex flex-col sm:flex-row lg:flex-col gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-6 shadow-sm">
              <Link to="/RoboticsCourseHP1Page">
                {isVi ? "KHÁM PHÁ KHÓA HP1" : "EXPLORE HP1 COURSE"}
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="border-border text-foreground font-semibold">
              <Link to="/FreeTrialContactPage">
                {isVi ? "Đăng ký kiểm tra đầu vào" : "Book Placement Test"}
              </Link>
            </Button>
          </div>
        </div>
      </Card>

      <Separator className="my-8" />

      {/* Course Sequence Navigation (Existing Next/Prev Navigation Preserved & Linked) */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          {/* Previous Course */}
          <Button asChild variant="outline" size="lg" className="h-auto py-3 px-5 justify-start text-left border-border hover:bg-muted/60">
            <Link to="/RoboticsCourseD3Page" className="inline-flex items-center gap-3">
              <ArrowLeft className="size-4 shrink-0 text-primary" />
              <div>
                <span className="block text-xs font-semibold uppercase text-muted-foreground">
                  {isVi ? "Học phần trước" : "Previous Course"}
                </span>
                <span className="text-sm sm:text-base font-bold text-foreground">
                  ← D3 — {isVi ? "Cỗ máy kỳ diệu" : "Amazing Machines"}
                </span>
              </div>
            </Link>
          </Button>

          {/* Next Course */}
          <div className="flex flex-col items-end gap-2 text-right">
            <Button asChild variant="default" size="lg" className="h-auto py-3 px-5 justify-end text-right shadow-sm">
              <Link to="/RoboticsCourseHP1Page" className="inline-flex items-center gap-3">
                <div className="text-right">
                  <span className="block text-xs font-semibold uppercase opacity-90 text-primary-foreground">
                    {isVi ? "Học phần tiếp theo" : "Next Course"}
                  </span>
                  <span className="text-sm sm:text-base font-bold text-primary-foreground">
                    HP1 — {isVi ? "Kỹ sư cơ khí nhí" : "Junior Mechanical"} →
                  </span>
                </div>
                <ArrowRight className="size-4 shrink-0" />
              </Link>
            </Button>
          </div>
        </div>

        {/* HP1 Prerequisite Note */}
        <div className="rounded-lg border border-border bg-muted/40 p-3.5 text-xs sm:text-sm text-muted-foreground flex items-center gap-2.5">
          <ShieldAlert className="size-4 text-primary shrink-0" />
          <span>
            {isVi ? "Lưu ý chuyển tiếp: Điều kiện đầu vào HP1 yêu cầu hoàn thành D4 hoặc vượt qua bài kiểm tra đánh giá năng lực cơ bản." : "Progression Notice: Entry to HP1 requires completing D4 or passing an entrance assessment."}
          </span>
        </div>
      </div>
    </div>;
}
export default function RoboticsDetail() {
  const wp_query_params = {
    post_type: "program",
    per_page: 1
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="roboticscoursed4detail" data-nav="light" className="bg-background text-foreground py-12 md:py-16 lg:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <PostsProvider wp_query={{
        post_type: "program",
        per_page: 1,
        status: ["publish"]
      }}>
          <CourseConsumer />
        </PostsProvider>
      </div>
    </section>;
}